/* Archivo: src/components/TrainerDashboard/TrainerDashboard.js */
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Chart from 'chart.js/auto';
import axios from 'axios';
import '../styles/TrainerDashboard.css';

import AthleteDashboard from '../AthleteDashboard/AthleteDashboard';
import AthleteProfile from '../AthleteProfile/AthleteProfile';
import TrainingZones from '../TrainingZones/TrainingZones';
import PMCPane from '../TrainerDashboard/PMCPane';
import PMCPaneAmCharts from '../TrainerDashboard/PMCPaneAmCharts';
import TrainerLibraryPanel from '../TrainingLibrary/TrainerLibraryPanel';
import DashboardGroupsSidebar from './DashboardGroupsSidebar';
import TrainerEmailPanel from './TrainerEmailPanel'; // panel de notificaciones (TP-like)
import WellnessVsCargaCard from './WellnessVsCarga/WellnessVsCargaCard';
import WellnessVsCargaRanking from './WellnessVsCarga/WellnessVsCargaRanking';
import { useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';

/* ====== Ajustes globales de Chart con guardia ====== */
const CHART_GLOBAL_FLAG = '__TZONE_TRAINER_DASHBOARD_CHARTS_INIT__';
if (typeof window !== 'undefined' && !window[CHART_GLOBAL_FLAG]) {
  Chart.defaults.font.size = 11;
  Chart.defaults.font.family = 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif';
  Chart.defaults.color = '#374151';
  Chart.defaults.plugins.legend.labels.boxWidth = 10;
  Chart.defaults.plugins.legend.labels.boxHeight = 10;
  window[CHART_GLOBAL_FLAG] = true;
}

/* ================= Config (sin import.meta) ================= */
const ENV_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined' && window.__CONFIG__ && window.__CONFIG__.API_BASE) ||
  `${API_BASE_URL}/api`;

const ENV_HOST =
  process.env.REACT_APP_HOST ||
  (typeof window !== 'undefined' && window.__CONFIG__ && window.__CONFIG__.HOST) ||
  API_BASE_URL;

const API_BASE = ENV_BASE;
const HOST = ENV_HOST;
const DEFAULT_AVATAR = `${HOST}/uploads/athletes/default.jpg`;

/** ✅ evita warning de <img src=""> */
function SafeImg({ src, alt, fallbackSrc, onError, ...rest }) {
  const s = src == null ? '' : String(src).trim();
  const fb = fallbackSrc == null ? '' : String(fallbackSrc).trim();
  const finalSrc = s || fb;

  // ✅ si no hay nada, no renderiza <img> (evita warning)
  if (!finalSrc) return null;

  const handleError = (e) => {
    // intenta fallback si el src falla
    if (fb && e?.currentTarget?.src !== fb) {
      e.currentTarget.src = fb;
    }
    if (typeof onError === 'function') onError(e);
  };

  return <img src={finalSrc} alt={alt || ''} onError={handleError} {...rest} />;
}

/* Axios client centralizado */
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 15000,
});

/* ================= Auth: adjuntar Bearer token si existe ================= */
function extractToken(value) {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  if (/^bearer\s+/i.test(raw)) return raw.replace(/^bearer\s+/i, "").trim();

  if (raw.startsWith("{") && raw.endsWith("}")) {
    try {
      const obj = JSON.parse(raw);
      const t = obj?.token || obj?.accessToken || obj?.jwt;
      if (t && String(t).trim()) return String(t).trim();
    } catch {}
  }

  return raw;
}

function pickTokenFromStorage() {
  const keys = ["token", "authToken", "accessToken", "jwt", "tzoneToken", "auth", "user"];
  for (const k of keys) {
    const v = localStorage.getItem(k);
    const t = extractToken(v);
    if (t) return t;
  }
  return null;
}

api.interceptors.request.use((config) => {
  const token = pickTokenFromStorage();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
/* ================ Helpers ================ */
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const num0 = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const firstDefined = (...vals) => vals.find((val) => val != null);
// eslint-disable-next-line no-unused-vars
const truncate = (s, n) => (s?.length > n ? s.slice(0, n - 1) + '…' : s || '');

/* ✅ Padding por medición real del texto (chart 1) con gap configurable */
const leftPadByMeasure = (ctx, labels, gap = 28) => {
  if (!ctx || !labels?.length) return 140;
  ctx.save();
  ctx.font = '12px system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif';
  let maxW = 0;
  labels.forEach((l) => {
    const m = ctx.measureText(String(l || ''));
    maxW = Math.max(maxW, m.width || 0);
  });
  ctx.restore();
  return Math.ceil(20 + gap + maxW + 20);
};

/* Imagen */
const looksLikeImage = (s) => {
  if (!s || typeof s !== 'string') return false;
  const v = s.trim();
  if (!v) return false;
  if (v.startsWith('data:image/')) return true;
  const lower = v.toLowerCase();
  if (lower.includes('/uploads/')) return true;
  if (/\.(png|jpg|jpeg|gif|webp|bmp|svg)(\?.*)?$/i.test(lower)) return true;
  if (/^https?:\/\//i.test(v)) return true;
  return false;
};

const normalizePhoto = (v, { scope = 'athlete' } = {}) => {
  if (!v) return '';
  const s = String(v).trim();
  if (!s) return '';
  if (s.startsWith('data:image/')) return s;
  if (s.startsWith('http')) return s;
  if (s.startsWith('/')) return `${HOST}${s}`;
  if (/^[\w.-]+\.(png|jpg|jpeg|gif|webp|bmp|svg)$/i.test(s)) {
    const base = scope === 'trainer' ? '/uploads/trainers/' : '/uploads/athletes/';
    return `${HOST}${base}${s}`;
  }
  return `${HOST}/${s}`;
};

const addCacheBuster = (url, key = null) => {
  if (!url || !key) return url || '';
  if (/\bt=/.test(url)) return url;
  const qs = `t=${encodeURIComponent(String(key))}`;
  return url.includes('?') ? `${url}&${qs}` : `${url}?${qs}`;
};

const normId = (v) => {
  if (v == null) return '';
  const s = String(v).trim();
  const m = s.match(/^ObjectId\("(.+)"\)$/i);
  return m ? m[1] : s;
};

const getAthletePhoto = (a) => {
  if (!a) return DEFAULT_AVATAR;
  if (looksLikeImage(a.__photoResolved)) {
    return addCacheBuster(
      normalizePhoto(a.__photoResolved, { scope: 'athlete' }),
      a?.updatedAt || a?.updated_at || a?.modifiedAt || ''
    );
  }
  const stamp = a?.updatedAt || a?.updated_at || a?.modifiedAt || '';
  const candidates = [
    a.profilePhoto, a.profilePicture, a.photoUrl, a.avatarUrl, a.avatar, a.imageUrl, a.picture, a.photo,
    a?.athlete?.profilePhoto, a?.athlete?.profilePicture, a?.athlete?.photoUrl,
    a?.user?.profile?.photoUrl, a?.user?.photoUrl,
    a?.accounts?.strava?.avatar, a?.accounts?.garmin?.avatar, a?.accounts?.polar?.avatar,
  ];
  for (const cand of candidates) {
    const raw = (cand ?? '').toString().trim();
    if (!raw) continue;
    if (looksLikeImage(raw) || /^[\w.-]+\.(?:png|jpe?g|gif|webp|bmp|svg)$/i.test(raw)) {
      const abs = normalizePhoto(raw, { scope: 'athlete' });
      return addCacheBuster(abs, stamp);
    }
  }
  return DEFAULT_AVATAR;
};

/* Porcentaje cumplimiento */
function computeWeeklyPercent({
  plannedMinutes = 0,
  completedMinutes = 0,
  plannedSessions = 0,
  completedSessions = 0,
}) {
  let v = 0;
  if (plannedSessions > 0) v = (num0(completedSessions) / num0(plannedSessions)) * 100;
  else if (plannedMinutes > 0) v = (num0(completedMinutes) / num0(plannedMinutes)) * 100;
  if (!Number.isFinite(v)) v = 0;
  return clamp(Math.round(v * 10) / 10, 0, 120);
}

/* ---------- HELPERS PARA TEMPLATES ---------- */
function normalizeActivitiesFromDays(input, fallbackSport = "running") {
  const byDay = new Map();
  let maxDay = 0;

  const pushAct = (dayNum, a) => {
    const d = Math.max(1, Number(dayNum ?? a?.day ?? a?.dayIndex ?? a?.offset ?? 1));
    maxDay = Math.max(maxDay, d);
    const norm = {
      day: d,
      sport: a?.sport || fallbackSport || "running",
      title: (a?.title || a?.name || "").trim() || "(sin título)",
      metric: a?.plannedType || a?.metric || "duration",
      value: String(a?.plannedValue ?? a?.value ?? a?.planned ?? ""),
      description: (a?.description || a?.notes || "").trim(),
    };
    if (!byDay.has(d)) byDay.set(d, []);
    byDay.get(d).push(norm);
  };

  const daysArr = (() => {
    if (!input) return [];
    if (Array.isArray(input.days)) return input.days;
    if (Array.isArray(input.weeks)) {
      const merged = [];
      input.weeks.forEach((w, wi) => {
        (w?.days || []).forEach((d, di) => {
          merged.push({ ...d, day: (wi * 7) + (Number(d?.day ?? di) + 1) });
        });
      });
      return merged;
    }
    if (Array.isArray(input)) return input;
    if (Array.isArray(input.activities)) {
      input.activities.forEach(a => pushAct(a?.day ?? a?.offset ?? a?.dayIndex ?? 1, a));
      return [];
    }
    if (input.plan?.days) return input.plan.days;
    if (input.template?.days) return input.template.days;
    return [];
  })();

  if (byDay.size > 0) return { byDay, daysCount: maxDay };

  for (const d of (Array.isArray(daysArr) ? daysArr : [])) {
    const rawDay = d?.day ?? d?.dayIndex ?? d?.offset ?? d?.index ?? d?.i ?? 1;
    const dayNum = Math.max(1, Number(rawDay) + (Number(rawDay) === 0 ? 1 : 0));

    const acts =
      Array.isArray(d?.activities) ? d.activities :
      Array.isArray(d?.workouts)   ? d.workouts   :
      [];

    if (acts.length === 0) {
      const maybeAct = d?.activity || d;
      if (maybeAct && (maybeAct.title || maybeAct.metric || maybeAct.plannedType || maybeAct.value != null)) {
        pushAct(dayNum, maybeAct);
        continue;
      }
    }

    for (const a of acts) pushAct(dayNum, a);
  }

  return { byDay, daysCount: maxDay };
}

async function fetchTemplateDetail(apiClient, tplId, fallbackSport = "running") {
  const tryOne = async (path, pick = (r) => r?.data) => {
    try {
      const res = await apiClient.get(path);
      const payload = pick(res) ?? {};
      const norm = normalizeActivitiesFromDays(payload, fallbackSport);
      if (norm.daysCount > 0 || norm.byDay.size > 0) return norm;
    } catch {
      // ignoramos y probamos la siguiente ruta
    }
    return null;
  };

  const tries = [
    [`/cycles/templates/${tplId}`, (r) => r?.data],
    [`/cycles/templates/${tplId}/days`, (r) => r?.data],
    [`/cycles/templates/${tplId}/activities`, (r) => r?.data],
    [`/templates/${tplId}`, (r) => r?.data],
    [`/templates/${tplId}/days`, (r) => r?.data],
    [`/templates/${tplId}/activities`, (r) => r?.data],
    [`/plans/${tplId}`, (r) => r?.data],
    [`/plans/${tplId}/days`, (r) => r?.data],
    [`/plans/${tplId}/activities`, (r) => r?.data],
    [`/trainer/plans/${tplId}`, (r) => r?.data],
    [`/trainer/plans/${tplId}/days`, (r) => r?.data],
    [`/trainer/plans/${tplId}/activities`, (r) => r?.data],
    [`/library/plans/${tplId}`, (r) => r?.data],
    [`/library/templates/${tplId}`, (r) => r?.data],
  ];

  for (const [path, pick] of tries) {
    const norm = await tryOne(path, pick);
    if (norm) return norm;
  }

  return { byDay: new Map(), daysCount: 0 };
}

/* ===== Validación canvas usable ===== */
const isCanvasUsable = (el) =>
  !!(el && el.ownerDocument && el.isConnected && typeof el.getContext === 'function' && document.contains(el));

/* ✅ Actualiza el chart solo si el canvas sigue en el DOM */
const safeChartUpdate = (chart, mode = 'none') => {
  try {
    if (!chart || chart._destroyed) return;
    const canvas = chart.canvas || chart?.ctx?.canvas;
    if (!canvas || !canvas.ownerDocument || !canvas.isConnected || !document.contains(canvas)) return;
    chart.update(mode);
  } catch {}
};

/* ============ Chart helpers/plugins ============ */
const safeChartDestroy = (ref) => {
  try {
    const ch = ref?.current;
    if (ch) {
      ch.stop?.();
      ch.unbindEvents?.();
      ch.destroy();
      ref.current = null;
    }
  } catch {}
};

/* ===== Plugin global: cancela operaciones si el canvas no está en el DOM ===== */
const guardDOMPlugin = {
  id: 'guardDOM',
  beforeInit(chart) {
    const c = chart?.canvas || chart?.ctx?.canvas;
    if (!c || !c.ownerDocument || !c.isConnected || !document.contains(c)) return false;
  },
  beforeResize(chart) {
    const c = chart?.canvas || chart?.ctx?.canvas;
    if (!c || !c.ownerDocument || !c.isConnected || !document.contains(c)) return false;
  },
  beforeUpdate(chart) {
    const c = chart?.canvas || chart?.ctx?.canvas;
    if (!c || !c.ownerDocument || !c.isConnected || !document.contains(c)) return false;
  },
  beforeEvent(chart) {
    const c = chart?.canvas || chart?.ctx?.canvas;
    if (!c || !c.ownerDocument || !c.isConnected || !document.contains(c)) return false;
  },
  beforeDraw(chart) {
    const c = chart?.canvas || chart?.ctx?.canvas;
    if (!c || !c.ownerDocument || !c.isConnected || !document.contains(c)) return false;
  },
};

/* ==== Avatares alineados al padding izquierdo visible ==== */
function makeAvatarYByIndex(photos) {
  const cache = {};
  return {
    id: 'avatarYByIndex',
    afterDatasetsDraw(chart) {
      if (!chart || chart._destroyed || !chart.ctx || !chart.scales) return;
      const { ctx, scales, chartArea } = chart;
      const yScale = scales?.y;
      if (!yScale || typeof yScale.getPixelForTick !== 'function' || yScale.left == null) return;

      ctx.save();
      photos.forEach((url, i) => {
        let img = cache[i];
        if (!img) {
          img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = url || DEFAULT_AVATAR;
          img.onerror = () => { img.src = DEFAULT_AVATAR; };
          img.onload = () => { safeChartUpdate(chart, 'none'); };
          cache[i] = img;
        }
        if (!img.complete || img.naturalWidth === 0) return;

        const cy = yScale.getPixelForTick(i);
        const cx = (chartArea?.left ?? yScale.left) - 22;
        const r = 10;

        ctx.save();
        ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.closePath(); ctx.clip();
        ctx.drawImage(img, cx - r, cy - r, r * 2, r * 2);
        ctx.restore();

        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
      });
      ctx.restore();
    },
  };
}

/* ✅ Plugin para dibujar etiquetas Y manuales */
function makeYLabelsPlugin(labels, { gap = 28 } = {}) {
  return {
    id: 'yLabelsByMeasure',
    afterDraw(chart) {
      const { ctx, scales } = chart;
      const y = scales?.y;
      if (!y || !labels?.length) return;
      ctx.save();
      ctx.font = '12px system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif';
      ctx.fillStyle = '#111827';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'right';
      const xText = (y.left || 0) - 20 - gap;
      labels.forEach((lbl, i) => {
        const py = y.getPixelForTick(i);
        ctx.fillText(String(lbl || ''), xText, py);
      });
      ctx.restore();
    },
  };
}

function makeAvatarPlugin(items, { size = 46, offset = 26, position = 'top' } = {}) {
  const cache = {};
  return {
    id: 'barAvatars',
    afterDatasetsDraw(chart) {
      if (!chart || chart._destroyed || !chart.ctx || !chart.canvas) return;
      const meta = chart.getDatasetMeta(0);
      if (!meta?.data?.length) return;

      const { ctx, chartArea } = chart;
      meta.data.forEach((el, i) => {
        if (!el) return;
        const x = el.x;
        const y = position === 'top'
          ? Math.max(chartArea.top + size / 2 + 2, (el.y ?? chartArea.top) - offset)
          : chartArea.bottom + offset;

        const url = items[i]?.photo || DEFAULT_AVATAR;

        let img = cache[i];
        if (!img) {
          img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = url;
          img.onerror = () => { if (img.src !== DEFAULT_AVATAR) img.src = DEFAULT_AVATAR; };
          img.onload = () => { safeChartUpdate(chart, 'none'); };
          cache[i] = img;
        }

        const r = size / 2;
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.25)';
        ctx.shadowBlur = 6;
        ctx.shadowOffsetY = 2;

        ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.closePath(); ctx.clip();
        if (img.complete && img.naturalWidth > 0) ctx.drawImage(img, x - r, y - r, size, size);
        else { ctx.fillStyle = '#e5e7eb'; ctx.fillRect(x - r, y - r, size, size); }
        ctx.restore();

        ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.strokeStyle = '#fff'; ctx.lineWidth = 3; ctx.stroke();
      });
    },
  };
}

function makeInsidePercentLabels(getPercentValue) {
  return {
    id: 'insidePercentLabels',
    afterDatasetsDraw(chart) {
      const { ctx } = chart;
      const meta0 = chart.getDatasetMeta(0);
      if (!meta0?.data?.length) return;

      ctx.save();
      ctx.font = 'bold 11px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      meta0.data.forEach((bar, i) => {
        if (!bar || typeof bar.x !== 'number' || typeof bar.y !== 'number') return;
        const val = typeof getPercentValue === 'function' ? getPercentValue(i) : null;
        if (val == null) return;
        const width = Math.abs(bar.base - bar.x);
        if (width < 24) return;
        const n = Number(val);
        const txt = Number.isFinite(n) ? `${(Math.round(n * 10) / 10).toFixed(1)}%` : `${val}%`;
        ctx.fillText(txt, (bar.base + bar.x) / 2, bar.y);
      });
      ctx.restore();
    },
  };
}

/* ======= PLUGINS para Chart 1 ======= */
const bands0Plugin = (ranges = []) => ({
  id: 'bands0',
  beforeDraw(chart) {
    const { ctx, chartArea, scales: { x } } = chart;
    if (!x) return;
    ctx.save();
    ranges.forEach(([from, to, fill]) => {
      const x1 = x.getPixelForValue(from);
      const x2 = x.getPixelForValue(to);
      ctx.fillStyle = fill;
      ctx.fillRect(Math.min(x1, x2), chartArea.top, Math.abs(x2 - x1), chartArea.bottom - chartArea.top);
    });
    const x0 = x.getPixelForValue(0);
    ctx.strokeStyle = '#7c3aed55';
    ctx.setLineDash([6, 4]);
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(x0, chartArea.top); ctx.lineTo(x0, chartArea.bottom); ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  },
});

const endValueLabels = {
  id: 'endValueLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const metaCTL = chart.getDatasetMeta(0);
    const metaATL = chart.getDatasetMeta(1);
    ctx.save();
    ctx.font = '12px system-ui, Segoe UI, Roboto, sans-serif';
    ctx.fillStyle = '#1f2937';
    [metaCTL, metaATL].forEach((m) => m?.data?.forEach((el, i) => {
      if (!el) return;
      const val = Math.round(chart.data.datasets[m.index].data[i] ?? 0);
      ctx.fillText(String(val), el.x + 6, el.y + 4);
    }));
    ctx.restore();
  },
};

const tsbDotsPlugin = {
  id: 'tsbDots',
  afterDatasetsDraw(chart) {
    const { ctx, data, scales: { x, y } } = chart;
    const ds = data.datasets.find((d) => d.yAxisID === 'tsb');
    if (!ds) return;
    ctx.save();
    ds.data.forEach((v, i) => {
      const px = x.getPixelForValue(v);
      const py = y.getPixelForTick(i);
      ctx.beginPath(); ctx.arc(px, py, 8, 0, Math.PI * 2);
      ctx.fillStyle = '#f59e0b';
      ctx.fill();
      ctx.lineWidth = 2; ctx.strokeStyle = '#fff'; ctx.stroke();
      ctx.fillStyle = '#7a4a00';
      ctx.font = 'bold 11px system-ui, Segoe UI, Roboto, sans-serif';
      const sign = v > 0 ? '+' : '';
      ctx.fillText(`${sign}${v}`, px - 10, py - 12);
    });
    ctx.restore();
  },
};

/* ========= Helper clave: POST con fallback ========= */
async function postWithFallback(apiClient, pathA, bodyA, pathB = null, bodyB = null) {
  try {
    return await apiClient.post(pathA, bodyA);
  } catch (err) {
    const status = err?.response?.status;
    if (pathB && (status === 400 || status === 404 || status === 405 || status === 409 || status === 422)) {
      return await apiClient.post(pathB, bodyB ?? bodyA);
    }
    throw err;
  }
}

/* ================ Componente ================ */
const TrainerDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const chartRef1 = useRef(null);
  const chartRef2 = useRef(null);
  const chartRef3 = useRef(null);
  const chartInstance1 = useRef(null);
  const chartInstance2 = useRef(null);
  const chartInstance3 = useRef(null);

  const [athletes, setAthletes] = useState([]);
  const [email, setEmail] = useState('');
  const [selectedAthleteId, setSelectedAthleteId] = useState(null);
  const [sidebarRightOpen, setSidebarRightOpen] = useState(true);

  // 👇 ESTE es el “modo de vista” de arriba (dashboard vs notificaciones)
  const [activeTopView, setActiveTopView] = useState('dashboard'); // 'dashboard' | 'notifications'

  // === TP-aligned: drop handlers para el calendario ===
  const handleCalendarDragOver = (e) => {
    if (
      e?.dataTransfer?.types?.includes('application/x-tzone-workout') ||
      e?.dataTransfer?.types?.includes('application/x-tzone-plan') ||
      e?.dataTransfer?.types?.includes('application/x-tzone-activity') ||
      e?.dataTransfer?.types?.includes('application/json') ||
      e?.dataTransfer?.types?.includes('text/plain')
    ) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleCalendarDrop = async (e) => {
    try {
      const types = e?.dataTransfer?.types || [];
      const get = (t) => (types.includes(t) ? e.dataTransfer.getData(t) : '');
      const raw =
        get('application/x-tzone-workout') ||
        get('application/x-tzone-plan') ||
        get('application/x-tzone-activity') ||
        get('application/json') ||
        get('text/plain');

      if (!raw) return;

      let payload = null;
      try { payload = JSON.parse(raw); } catch { payload = null; }
      if (!payload || !payload.type) {
        payload = { type: 'workout', id: String(raw).trim() };
      }

      const el = document.elementFromPoint(e.clientX, e.clientY);
      const cell = el?.closest?.('[data-date]') || e.target.closest?.('[data-date]');
      const date = cell?.getAttribute?.('data-date');

      if (!date) {
        console.warn('[drop] No se encontró data-date. Agrega data-date="YYYY-MM-DD" a las celdas del calendario.');
        alert(t('trainerDashboard.dropDateNotDetected'));
        return;
      }

      if (!selectedAthleteId) {
        alert(t('trainerDashboard.selectAthleteBeforeAssign'));
        return;
      }

      const toHHMMSS = (minutes = 60) => {
        const m = Math.max(0, parseInt(minutes, 10) || 0);
        const h = String(Math.floor(m / 60)).padStart(2, '0');
        const mm = String(m % 60).padStart(2, '0');
        return `${h}:${mm}:00`;
      };
      const mapSportEs = (s = '') => {
        const k = String(s).toLowerCase();
        if (['run','running','correr'].includes(k)) return 'correr';
        if (['bike','cycling','ciclismo'].includes(k)) return 'ciclismo';
        if (['swim','swimming','nadar','natación','natacion'].includes(k)) return 'nadar';
        if (['strength','fuerza','gym'].includes(k)) return 'fuerza';
        if (['walk','walking','caminar'].includes(k)) return 'caminar';
        if (['rest','descanso'].includes(k)) return 'descanso';
        return 'otro';
      };
      const buildStartEnd = (isoDate) => {
        const start = new Date(`${isoDate}T06:00:00`);
        const end = new Date(start.getTime() + 60 * 60 * 1000);
        return { start, end };
      };

      if (payload.type === 'plan') {
        const detail = await fetchTemplateDetail(api, payload.id);
        if (!detail.daysCount) {
          alert(t('trainerDashboard.templateReadError'));
          return;
        }

        const addDays = (iso, d) => {
          const dt = new Date(`${iso}T06:00:00`);
          dt.setDate(dt.getDate() + (d - 1));
          return dt.toISOString().slice(0,10);
        };
        const toHHMMSSLocal = (minutes = 60) => {
          const m = Math.max(0, parseInt(minutes, 10) || 0);
          const h = String(Math.floor(m / 60)).padStart(2, '0');
          const mm = String(m % 60).padStart(2, '0');
          return `${h}:${mm}:00`;
        };

        const tasks = [];
        for (const [day, acts] of detail.byDay.entries()) {
          const targetISO = addDays(date, Number(day) || 1);

          acts.forEach((act) => {
            const { start, end } = buildStartEnd(targetISO);
            const type = mapSportEs(act.sport || 'correr');

            const bodyCalendar = {
              athleteId: selectedAthleteId,
              date: targetISO,
              source: { type: 'tpl_workout', id: String(payload.id) },
              payload: {
                overrides: {
                  title: act.title || '',
                  sport: type,
                  plannedType: act.metric || 'duration',
                  plannedValue: (act.value ?? '').toString(),
                  description: act.description || '',
                },
              },
            };

            const planned = {};
            if ((act.metric === 'duration' || !act.metric) && act.value) planned.duracion = toHHMMSSLocal(act.value);
            if (act.metric === 'distance' && act.value) planned.distancia = String(act.value);

            const bodyActivities = {
              assignedTo: selectedAthleteId,
              type,
              title: act.title || 'Sesión',
              trainer: localStorage.getItem('userId') || null,
              date: start,
              start,
              end,
              description: act.description || '',
              preComments: '',
              postComments: '',
              icon: '',
              bg_color: '',
              draggable: true,
              external: true,
              planned,
              completed: {},
            };

            tasks.push(
              postWithFallback(api, `/calendar/activities`, bodyCalendar, `/activities`, bodyActivities)
            );
          });
        }

        const results = await Promise.allSettled(tasks);
        const ok = results.filter(r => r.status === 'fulfilled').length;
        const fail = results.length - ok;

        if (ok) window.dispatchEvent(new Event('activities:refresh'));
        alert(
          t('trainerDashboard.planAppliedResult', {
            ok,
            fail: fail ? t('trainerDashboard.failedCount', { fail }) : ''
          })
        );
      } else {
        const { start, end } = buildStartEnd(date);
        const type = mapSportEs(payload.sport || 'correr');

        const bodyCalendar = {
          athleteId: selectedAthleteId,
          date,
          source: payload.id
            ? { type: 'tpl_workout', id: String(payload.id) }
            : { type: 'free' },
          payload: {
            overrides: {
              title: payload.name || '',
              sport: type,
              plannedType: payload.metric || 'duration',
              plannedValue: (payload.value ?? '').toString(),
              description: payload.description || '',
            },
          },
        };

        const planned = {};
        if ((payload.metric === 'duration' || !payload.metric) && payload.value) planned.duracion = toHHMMSS(payload.value);
        if (payload.metric === 'distance' && payload.value) planned.distancia = String(payload.value);

        const bodyActivities = {
          assignedTo: selectedAthleteId,
          type,
          title: payload.name || 'Sesión',
          trainer: localStorage.getItem('userId') || null,
          date: start,
          start,
          end,
          description: payload.description || '',
          preComments: '',
          postComments: '',
          icon: '',
          bg_color: '',
          draggable: true,
          external: true,
          planned,
          completed: {},
        };

        await postWithFallback(
          api,
          `/calendar/activities`, bodyCalendar,
          `/activities`,           bodyActivities
        );
      }

      window.dispatchEvent(new Event('activities:refresh'));
    } catch (err) {
      console.error('Drop error', err);
      const msg = err?.response?.data?.message || err?.message || t('trainerDashboard.unknownError');
      alert(t('trainerDashboard.assignError', { message: msg }));
    }
  };

  const [selectedAthleteInfo, setSelectedAthleteInfo] = useState(null);
  const [activeSection, setActiveSection] = useState('calendar');
  const [refreshTick, setRefreshTick] = useState(0);

  const [pmcView, setPmcView] = useState('both');
  const [pmcTop, setPmcTop] = useState([]);
  const [pmcLoading, setPmcLoading] = useState(false);
  const [pmcError, setPmcError] = useState('');

  const [cmpTop, setCmpTop] = useState([]);
  const [cmpLoading, setCmpLoading] = useState(false);
  const [cmpError, setCmpError] = useState('');

  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState('');

  const [trainerId, setTrainerId] = useState(() => localStorage.getItem('userId') || '');

  useEffect(() => {
    const sync = () => setTrainerId(localStorage.getItem('userId') || '');
    window.addEventListener('storage', sync);
    // también por si el login lo setea en la misma pestaña:
    const tmr = setInterval(sync, 500);
    return () => {
      window.removeEventListener('storage', sync);
      clearInterval(tmr);
    };
  }, []);

  /* Mapa maestro de atletas */
  const athMap = useMemo(() => {
    const m = new Map();
    athletes.forEach((a) => {
      const id = normId(a._id);
      const name = `${a.firstName || ''} ${a.lastName || ''}`.trim() || 'Atleta';
      const photo = getAthletePhoto(a);
      if (id) m.set(id, { name, photo });
    });
    return m;
  }, [athletes]);

  const hasAthletes = Array.isArray(athletes) && athletes.length > 0;

  /* Datos sintéticos fallback */
  const synthTop = useCallback(() => {
    const names = ['Ana', 'Luis', 'Carla', 'Marcos', 'Julia', 'Diego', 'Rosa', 'Paco', 'Irene', 'Tomás'];
    return names.map((n, i) => {
      const ctl = 25 + 4 * (10 - i) + Math.round(Math.random() * 6);
      const atl = ctl + (Math.round(Math.random() * 12) - 6);
      const tsb = ctl - atl;
      const acwr = +(((atl || 1) / Math.max(1, ctl || 28)) + 0.6).toFixed(2);
      return { athleteId: `synthetic-${i}`, name: n, photo: DEFAULT_AVATAR, ctl: Math.max(0, ctl), atl: Math.max(0, atl), tsb, acwr };
    });
  }, []);

  const synthCompliance = useCallback(() => {
    const names = ['Ana','Luis','Carla','Marcos','Julia','Diego','Rosa','Paco','Irene','Tomás'];
    return names.map((n,i) => {
      const planned = 300 + Math.round(Math.random()*180);
      const completed = Math.max(0, Math.round(planned * (0.5 + Math.random()*0.6)));
      const percent = clamp(Math.round((completed / Math.max(1, planned)) * 100), 0, 120);
      return { athleteId: `synthetic-${i}`, name: n, photo: DEFAULT_AVATAR, plannedMinutes: planned, completedMinutes: completed, percent };
    });
  }, []);

  /* ---- fetch atletas ---- */
  useEffect(() => {
    if (!trainerId) return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const { data } = await api.get(`/activities/trainer/${trainerId}/athletes`, { signal: ctrl.signal });
        setAthletes(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
          console.error('Error al obtener atletas:', e);
          setAthletes([]);
        }
      }
    })();
    return () => ctrl.abort();
  }, [trainerId]);

  /* Limpiar selección previa al montar — el overview es la pantalla de inicio */
  useEffect(() => {
    localStorage.removeItem('selectedAthleteId');
    localStorage.removeItem('selectedAthleteName');
    localStorage.removeItem('selectedAthletePhoto');
  }, []);

  /* {t('trainerDashboard.sendInvite')} */
  const handleSendInvite = async () => {
    if (!email || !trainerId) {
      alert(t('trainerDashboard.missingInviteData'));
      return;
    }
    try {
      await api.post(`/invitations/send`, { trainerId, athleteEmail: email });
      alert(t('trainerDashboard.inviteSent'));
      setEmail('');
    } catch (error) {
      console.error('Error al enviar invitación:', error);
      alert(error.response?.data?.message || t('trainerDashboard.inviteError'));
    }
  };

  /* Perfil entrenador */
  const [trainerProfile, setTrainerProfile] = useState({ firstName: '', lastName: '', profilePicture: '' });
  useEffect(() => {
    if (!trainerId) return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const { data } = await api.get(`/trainers/profile/${trainerId}`, { signal: ctrl.signal });
        const trainer = data?.trainer || {};
        setTrainerProfile({
          firstName: trainer.firstName || '',
          lastName: trainer.lastName || '',
          profilePicture: trainer.profilePhoto
            ? normalizePhoto(trainer.profilePhoto, { scope: 'trainer' })
            : `${HOST}/uploads/trainers/default.jpg`,
        });
      } catch (error) {
        if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
          console.error('❌ Error al obtener perfil del entrenador:', error);
        }
      }
    })();
    return () => ctrl.abort();
  }, [trainerId]);

  /* ===== PMC Top10 ===== */
  useEffect(() => {
    if (!trainerId || selectedAthleteId) return;

    const ctrl = new AbortController();
    setPmcError('');
    setPmcLoading(true);

    (async () => {
      try {
        // ✅ Si no hay atletas, no tiene sentido llamar analytics
        if (!hasAthletes) {
          setPmcTop([]);
          setPmcError('');
          return;
        }

        const { data } = await api.get(`/analytics/trainer/${trainerId}/pmc-top`, {
          params: { limit: 10 },
          signal: ctrl.signal,
        });

        const rows = Array.isArray(data) ? data : [];

        const normalized = rows.map((r) => {
          const athleteId = normId(
            r.athleteId ?? r.athlete_id ?? r.userId ?? r.user_id ?? r.athleteIdStr ?? r.athlete ?? r._id ?? r.id
          );

          const endpointPhoto =
            r.profilePhoto || r.photo || r.profilePicture || r.photoUrl ||
            r?.athlete?.profilePhoto || r?.athlete?.profilePicture || r?.athlete?.photoUrl || '';

          const ctl = num0(firstDefined(r.ctl, r?.metrics?.ctl, 0));
          const atl = num0(firstDefined(r.atl, r?.metrics?.atl, 0));
          const tsb = num0(firstDefined(r.tsb, r?.metrics?.tsb, ctl - atl));
          const acwr = num0(firstDefined(r.acwr, r?.metrics?.acwr, 0));

          const fromMap = athMap.get(athleteId);
          const name =
            (r.name && r.name.trim() && r.name.trim().toLowerCase() !== 'atleta' ? r.name.trim() : '') ||
            (fromMap?.name ?? '');

          const photo =
            (looksLikeImage(endpointPhoto) ? normalizePhoto(endpointPhoto, { scope: 'athlete' }) : '') ||
            (fromMap?.photo ?? '');

          return { athleteId, name, photo, ctl, atl, tsb, acwr };
        });

        // ✅ Filtra SOLO atletas que realmente están en tu lista de atletas
        const allowedIds = new Set([...athMap.keys()]);
        const unique = new Map();

        for (const r of normalized) {
          const id = normId(r.athleteId);
          if (!id) continue;
          if (!allowedIds.has(id)) continue;

          const cur = unique.get(id);
          if (!cur || Math.abs(r.tsb) > Math.abs(cur.tsb)) unique.set(id, r);
        }

        const list = Array.from(unique.values());

        // ✅ Si hay atletas pero el endpoint no devolvió nada utilizable
        if (list.length === 0) {
          setPmcTop([]);
          setPmcError(t('trainerDashboard.noPerformanceData'));
          return;
        }

        setPmcTop(list.sort((a, b) => b.tsb - a.tsb).slice(0, 10));
        setPmcError('');
      } catch (e) {
        if (e.name === 'CanceledError' || e.name === 'AbortError') return;

        // ✅ Limpio: no usamos synthTop() nunca más
        setPmcTop([]);

        // Mensaje claro pero no ruidoso
        const status = e?.response?.status;
        const msg = status
          ? t('trainerDashboard.performanceLoadHttpError', { status })
          : t('trainerDashboard.performanceLoadNoResponse');

        setPmcError(hasAthletes ? msg : '');
        console.warn('[pmc-top] error:', status, e?.message || e);
      } finally {
        setPmcLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [trainerId, selectedAthleteId, hasAthletes, athMap, synthTop, t]);

  /* ===== Cumplimiento Top10 ===== */
  useEffect(() => {
    if (!trainerId || selectedAthleteId) return;
    const ctrl = new AbortController();
    setCmpError('');
    setCmpLoading(true);

    (async () => {
      try {
        const { data } = await api.get(`/analytics/trainer/${trainerId}/compliance-top`, {
          params: { limit: 10, period: 'week' },
          signal: ctrl.signal,
        });

        const rows = Array.isArray(data) ? data : [];
        const unique = new Map();

        rows.forEach((r) => {
          const athleteId = normId(r.athleteId || r.athlete_id || r.userId || r.user_id || r._id || r.id);
          if (!athleteId) return;

          const fromMap = athMap.get(athleteId);
          const name = (r.name && r.name.trim()) || (fromMap?.name ?? 'Atleta');

          const endpointPhoto = r.profilePhoto || r.photo || r.profilePicture || r.photoUrl || '';
          const photo =
            (looksLikeImage(endpointPhoto) ? normalizePhoto(endpointPhoto, { scope: 'athlete' }) : '') ||
            (fromMap?.photo ?? DEFAULT_AVATAR);

          const plannedMinutes    = num0(firstDefined(r.plannedMinutes, 0));
          const completedMinutes  = num0(firstDefined(r.completedMinutes, 0));
          const plannedSessions   = num0(firstDefined(r.plannedSessions, 0));
          const completedSessions = num0(firstDefined(r.completedSessions, 0));

          const percent = computeWeeklyPercent({
            plannedMinutes, completedMinutes, plannedSessions, completedSessions,
          });

          const row = {
            athleteId, name, photo,
            plannedMinutes, completedMinutes, plannedSessions, completedSessions,
            percent,
          };

          const prev = unique.get(athleteId);
          if (!prev || (prev.percent === 0 && percent > 0)) unique.set(athleteId, row);
        });

        let list = Array.from(unique.values());
        const allowedIds = new Set(athMap.keys());
        list = list.filter(r => allowedIds.has(normId(r.athleteId)));

        await Promise.allSettled(
          list.map(async (row, idx) => {
            try {
              const { data: c } = await api.get(`/analytics/athlete/${row.athleteId}/compliance`, {
                params: { trainerId },
                signal: ctrl.signal,
              });

              const plannedMinutes    = num0(firstDefined(c?.plannedMinutes, row.plannedMinutes));
              const completedMinutes  = num0(firstDefined(c?.completedMinutes, row.completedMinutes));
              const plannedSessions   = num0(firstDefined(c?.plannedSessions, row.plannedSessions));
              const completedSessions = num0(firstDefined(c?.completedSessions, row.completedSessions));

              const percent = computeWeeklyPercent({
                plannedMinutes, completedMinutes, plannedSessions, completedSessions,
              });

              list[idx] = { ...row, plannedMinutes, completedMinutes, plannedSessions, completedSessions, percent };
            } catch { /* ignore */ }
          })
        );

        if (list.length === 0) {
          list = synthCompliance();
          setCmpError(t('trainerDashboard.showingSampleCompliance'));
        }

        setCmpTop(list.sort((a, b) => b.percent - a.percent).slice(0, 10));
      } catch (e) {
        if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
          console.warn('Sin endpoint compliance-top; mostrando placeholders', e?.message || e);
          setCmpTop(synthCompliance());
          setCmpError(t('trainerDashboard.showingSampleCompliance'));
        }
      } finally {
        setCmpLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [trainerId, selectedAthleteId, synthCompliance, athMap, t]);

  /* ===== Datos listos para pintar (PMC Top) ===== */
  const rowsForDisplay = useMemo(() => {
    return pmcTop.map((r) => {
      const id = normId(r.athleteId);
      const fromMap = athMap.get(id) || {};
      const endpointName =
        r.name && r.name.trim() && r.name.trim().toLowerCase() !== 'atleta'
          ? r.name.trim()
          : '';
      const name = endpointName || fromMap.name || 'Atleta';
      const photo =
        fromMap.photo ||
        (looksLikeImage(r.photo) ? normalizePhoto(r.photo, { scope: 'athlete' }) : '') ||
        DEFAULT_AVATAR;
      return { ...r, name, photo };
    });
  }, [pmcTop, athMap]);

  /* ===== Datos listos para pintar (Cumplimiento Top) ===== */
  const cmpRowsForDisplay = useMemo(() => {
    return cmpTop.map((r) => {
      const id = normId(r.athleteId);
      const fromMap = athMap.get(id) || {};
      const endpointName =
        r.name && r.name.trim() && r.name.trim().toLowerCase() !== 'atleta'
          ? r.name.trim()
          : '';
      const name = endpointName || fromMap.name || 'Atleta';
      const photo =
        fromMap.photo ||
        (looksLikeImage(r.photo) ? normalizePhoto(r.photo, { scope: 'athlete' }) : '') ||
        DEFAULT_AVATAR;
      const percent = clamp(Math.round(num0(r.percent) * 10) / 10, 0, 120);
      return { ...r, name, photo, percent };
    });
  }, [cmpTop, athMap]);

  /* ===== Charts: PMC y ACWR ===== */
  const rafCharts12 = useRef(0);
  useEffect(() => {
    if (selectedAthleteId) return;
    safeChartDestroy(chartInstance1);
    safeChartDestroy(chartInstance2);
    if (!rowsForDisplay?.length) return;

    if (rafCharts12.current) cancelAnimationFrame(rafCharts12.current);

    rafCharts12.current = requestAnimationFrame(() => {
      const c1 = chartRef1.current;
      const c2 = chartRef2.current;

      if (!isCanvasUsable(c1) || !isCanvasUsable(c2)) return;

      const labels = rowsForDisplay.map((r) => r.name);
      const ctl = rowsForDisplay.map((r) => r.ctl);
      const atl = rowsForDisplay.map((r) => r.atl);
      const tsb = rowsForDisplay.map((r) => r.tsb);
      const acwr = rowsForDisplay.map((r) => r.acwr);

      // === Chart 1
      try {
        if (!isCanvasUsable(c1)) return;
        const ctx1 = c1.getContext('2d');
        if (!ctx1) return;

        c1.height = Math.max(220, labels.length * 28);

        const GAP_Y = 36;
        const leftPadCanvas = Math.max(120, leftPadByMeasure(ctx1, labels, GAP_Y));

        const photos1 = rowsForDisplay.map(r => r.photo || DEFAULT_AVATAR);
        const avatarYPlugin = makeAvatarYByIndex(photos1);
        const yLabelsPlugin = makeYLabelsPlugin(labels, { gap: GAP_Y });

        const ranges = [
          [-60, -20, '#fee2e299'],
          [-20, -10, '#fde68a66'],
          [-10,   0, '#ecfccb66'],
          [  0,  10, '#dbeafe66'],
          [ 10,  40, '#e5f0ff33'],
        ];

        const ch1 = new Chart(ctx1, {
          type: 'bar',
          data: {
            labels,
            datasets: [
              { type: 'bar', label: 'CTL (42d)', data: ctl, backgroundColor: '#1f2c46', borderColor: '#1f2c46', borderWidth: 0, barThickness: 16 },
              { type: 'bar', label: 'ATL (7d)',  data: atl, backgroundColor: '#cbd5e1', borderColor: '#cbd5e1', borderWidth: 0, barThickness: 16 },
              { type: 'scatter', label: 'TSB (punto)', yAxisID: 'tsb', data: tsb, parsing: false, pointRadius: 0, showLine: false },
            ],
          },
          options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { left: leftPadCanvas, right: 8, top: 6, bottom: 6 } },
            plugins: {
              legend: { position: 'top', labels: { font: { size: 10 }, boxWidth: 12, padding: 8 } },
              tooltip: { mode: 'nearest', intersect: false },
            },
            scales: {
              x: {
                beginAtZero: true,
                min: -65,
                max: Math.max(40, Math.ceil(Math.max(...ctl, ...atl) * 1.05)),
                grid: { color: 'rgba(0,0,0,0.05)' },
                title: { display: true, text: 'Carga / puntos', font: { size: 9 } },
              },
              y: {
                ticks: { display: false },
                grid: { display: false },
              },
              tsb: { type: 'linear', position: 'top', min: -65, max: 40, display: false },
            },
            animation: { duration: 250 },
          },
          plugins: [
            guardDOMPlugin,
            avatarYPlugin,
            yLabelsPlugin,
            bands0Plugin(ranges),
            endValueLabels,
            tsbDotsPlugin,
          ],
        });

        if (!isCanvasUsable(ch1.canvas || ch1?.ctx?.canvas)) {
          try { ch1.destroy(); } catch {}
          chartInstance1.current = null;
        } else {
          chartInstance1.current = ch1;
        }
      } catch (err) {
        console.warn('Chart1 creation skipped (canvas detached):', err);
      }

      // === Chart 2: ACWR
      try {
        if (!isCanvasUsable(c2)) return;
        const ctx2 = c2.getContext('2d');
        if (!ctx2) return;
        const colors = acwr.map((v) =>
          v < 0.8 ? 'rgba(239,68,68,0.75)' : v <= 1.3 ? 'rgba(16,185,129,0.85)' : v <= 1.5 ? 'rgba(245,158,11,0.85)' : 'rgba(239,68,68,0.85)'
        );
        const avatarItems = rowsForDisplay.map((r) => ({ photo: r.photo }));
        const ch2 = new Chart(ctx2, {
          type: 'bar',
          data: { labels, datasets: [{ label: 'ACWR (7d / 28d)', data: acwr, backgroundColor: colors, borderRadius: 6, barPercentage: 0.6, categoryPercentage: 0.7 }] },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { top: 56, bottom: 8 } },
            plugins: { legend: { display: false }, tooltip: { mode: 'nearest', intersect: false } },
            scales: {
              y: { beginAtZero: true, min: 0, suggestedMax: Math.max(1.6, Math.ceil(Math.max(...acwr, 1.6) * 1.1)), ticks: { stepSize: 0.2 }, grid: { color: 'rgba(0,0,0,0.05)' } },
              x: { grid: { display: false }, ticks: { maxRotation: 0, minRotation: 0 } }
            },
            animation: { duration: 250 },
          },
          plugins: [
            guardDOMPlugin,
            makeAvatarPlugin(avatarItems, { size: 46, offset: 26, position: 'top' }),
          ],
        });
        if (!isCanvasUsable(ch2.canvas || ch2?.ctx?.canvas)) {
          try { ch2.destroy(); } catch {}
          chartInstance2.current = null;
        } else {
          chartInstance2.current = ch2;
        }
      } catch (err) {
        console.warn('Chart2 creation skipped (canvas detached):', err);
      }
    });

    return () => {
      if (rafCharts12.current) cancelAnimationFrame(rafCharts12.current);
      safeChartDestroy(chartInstance1);
      safeChartDestroy(chartInstance2);
    };
  }, [selectedAthleteId, rowsForDisplay]);

  /* ===== Chart: Cumplimiento Top10 ===== */
  const rafChart3 = useRef(0);
  useEffect(() => {
    if (selectedAthleteId) return;
    safeChartDestroy(chartInstance3);
    if (!cmpRowsForDisplay?.length) return;

    if (rafChart3.current) cancelAnimationFrame(rafChart3.current);

    rafChart3.current = requestAnimationFrame(() => {
      const c3 = chartRef3.current;
      if (!isCanvasUsable(c3)) return;

      const labels = cmpRowsForDisplay.map(r => r.name);
      const perc   = cmpRowsForDisplay.map(r => r.percent);
      const remain = perc.map(v => Math.max(0, 100 - clamp(Number(v) || 0, 0, 100)));

      try {
        if (!isCanvasUsable(c3)) return;
        const ctx3 = c3.getContext('2d');
        if (!ctx3) return;

        c3.height = Math.max(220, labels.length * 24);

        const gradient = ctx3.createLinearGradient(0, 0, 300, 0);
        gradient.addColorStop(0, '#f59e0b');
        gradient.addColorStop(1, '#1f2937');

        const photos3 = cmpRowsForDisplay.map(r => r.photo || DEFAULT_AVATAR);
        const avatarY = makeAvatarYByIndex(photos3);
        const insideLabels = makeInsidePercentLabels((i) => perc[i]);

        const ch3 = new Chart(ctx3, {
          type: 'bar',
          data: {
            labels,
            datasets: [
              { label: t('trainerDashboard.completedLabel'), data: perc.map(v => clamp(Number(v) || 0, 0, 100)), backgroundColor: gradient, borderRadius: 8, stack: 'p', barPercentage: 0.7, categoryPercentage: 0.85 },
              { label: t('trainerDashboard.remainingLabel'), data: remain, backgroundColor: '#e5e7eb', borderRadius: 8, stack: 'p', barPercentage: 0.7, categoryPercentage: 0.85 },
            ],
          },
          options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { left: 56, right: 8, top: 6, bottom: 6 } },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  title: (items) => items?.[0]?.label || '',
                  label: (item) => {
                    const r = cmpRowsForDisplay[item.dataIndex];
                    const pctTxt = Number.isFinite(Number(r.percent))
                      ? `${(Math.round(Number(r.percent) * 10) / 10).toFixed(1)}%`
                      : `${r.percent}%`;
                    if (r.plannedSessions) return ` ${pctTxt} · ${r.completedSessions||0}/${r.plannedSessions||0} ${t('trainerDashboard.sessionsLabel')}`;
                    return ` ${pctTxt} · ${r.completedMinutes||0}/${r.plannedMinutes||0} ${t('trainerDashboard.minutesLabel')}`;
                  },
                },
              },
            },
            scales: {
              x: { min: 0, max: 100, ticks: { callback: (v) => `${v}%` }, grid: { color: 'rgba(0,0,0,0.05)' } },
              y: { ticks: { padding: 28, autoSkip: false, maxTicksLimit: labels.length, callback: (_, i) => labels[i], font: { size: 11 } }, grid: { display: false } },
            },
            animation: { duration: 250 },
          },
          plugins: [
            guardDOMPlugin,
            avatarY,
            insideLabels,
          ],
        });

        if (!isCanvasUsable(ch3.canvas || ch3?.ctx?.canvas)) {
          try { ch3.destroy(); } catch {}
          chartInstance3.current = null;
        } else {
          chartInstance3.current = ch3;
        }
      } catch (err) {
        console.warn('Chart3 creation skipped (canvas detached):', err);
      }
    });

    return () => {
      if (rafChart3.current) cancelAnimationFrame(rafChart3.current);
      safeChartDestroy(chartInstance3);
    };
  }, [selectedAthleteId, cmpRowsForDisplay, t]);

  /* === Redibujo seguro === */
  useEffect(() => {
    const parents = [
      chartRef1.current?.parentElement,
      chartRef2.current?.parentElement,
      chartRef3.current?.parentElement,
    ].filter(Boolean);

    if (!parents.length) return;

    const safeResize = (chart) => {
      if (!chart) return;
      const canvas = chart.canvas || chart?.ctx?.canvas;
      if (!isCanvasUsable(canvas)) return;
      try { chart.resize(); } catch {}
    };

    const handler = () => {
      safeResize(chartInstance1.current);
      safeResize(chartInstance2.current);
      safeResize(chartInstance3.current);
    };

    const ro = new ResizeObserver(handler);
    parents.forEach((p) => ro.observe(p));

    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
    };
  }, []);

  // eslint-disable-next-line no-unused-vars
  const handleApplyTemplate = async (payload) => {
    try {
      // 👇 le decimos al backend: "además de crear las actividades, mándalas por correo"
      const body = {
        ...payload,
        notifyOnCreate: true,          // <- front pide correos
        emailMode: 'assigned_activity' // <- opcional, por si quieres diferenciarlo en el backend
      };

      await postWithFallback(api, `/cycles/apply-template`, body, `/plans/instantiate`, body);

      if (
        selectedAthleteId &&
        Array.isArray(payload.athleteIds) &&
        payload.athleteIds.map(String).includes(String(selectedAthleteId))
      ) {
        setRefreshTick((tick) => tick + 1);
        setActiveSection('calendar');
      }

      alert(
        t('trainerDashboard.planAppliedResult', {
          ok: payload.athleteIds?.length || 0,
          fail: ''
        })
      );
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404) {
        alert(t('trainerDashboard.applyTemplateEndpointMissing'));
      } else {
        alert(
          t('trainerDashboard.applyTemplateError', {
            message: err?.response?.data?.message || err.message
          })
        );
      }
      console.error('[TrainerDashboard] apply-template error:', err);
    }
  };

  const probeIA = async () => {
    setAiLoading(true);
    setAiText('');
    try {
      const payload = { athleteId: selectedAthleteId };
      const { data } = await api.post(`/analytics/pmc/ai-insights`, payload);
      setAiText(data?.text || JSON.stringify(data, null, 2));
    } catch (e) {
      setAiText(
        t('trainerDashboard.aiError', {
          message: e?.response?.data?.error || e.message
        })
      );
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="trainer-dashboard">
      <header className="td-header">
        <div className="td-header-brand">
          <SafeImg
            src={trainerProfile.profilePicture}
            fallbackSrc={`${HOST}/uploads/trainers/default.jpg`}
            alt={t('trainerDashboard.trainerPhotoAlt')}
            className="td-header-avatar"
          />
          <span className="td-header-name">{`${trainerProfile.firstName} ${trainerProfile.lastName}`}</span>
        </div>

        <h1 className="td-header-title">{t('trainerDashboard.title')}</h1>

        <nav className="td-header-nav">
          <Link to="/trainer-athletes" className="td-nav-btn">{t('trainerDashboard.manageAthletes')}</Link>
          <Link to="/trainer-profile" className="td-nav-btn">{t('trainerDashboard.viewProfile')}</Link>
          <Link to="/trainer-plans" className="td-nav-btn">{t('trainerDashboard.plans')}</Link>
          <Link to="/trainer-groups" className="td-nav-btn">{t('trainerDashboard.athleteGroup')}</Link>

          <button type="button" onClick={() => navigate('/social')} className="td-nav-btn td-nav-btn--community">
            {t('trainerDashboard.community')}
          </button>

          <button
            type="button"
            onClick={() => setActiveTopView('notifications')}
            className={`td-nav-btn${activeTopView === 'notifications' ? ' td-nav-btn--active' : ''}`}
          >
            {t('trainerDashboard.notifications')}
          </button>

          <button
            type="button"
            onClick={() => { localStorage.clear(); window.location.href = '/login'; }}
            className="td-nav-btn td-nav-btn--danger"
          >
            {t('trainerDashboard.logout')}
          </button>
        </nav>
      </header>

      {activeTopView === 'notifications' ? (
        <div className="td-full-panel-wrapper td-full-panel-wrapper--notifications">
          <div className="email-panel-shell">
            <TrainerEmailPanel
              trainerId={trainerId}
              onBack={() => setActiveTopView('dashboard')}
            />
            <div className="email-panel-footer mt-3">
              <button
                onClick={() => setActiveTopView('dashboard')}
                style={{ background:'#193255', color:'#fff', border:'none', borderRadius:999, padding:'8px 20px', fontSize:13, fontWeight:700, cursor:'pointer' }}
              >
                {t('trainerDashboard.backToDashboard')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className={`container-fluid mt-3 td-layout${sidebarRightOpen ? '' : ' td-layout--sidebar-collapsed'}`}>
          {/* Izquierda */}
          <aside className="td-left">
            <div className="td-card">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <strong>{t('trainerDashboard.myAthletes', { count: athletes.length })}</strong>
                {selectedAthleteId && (
                  <button
                    style={{ fontSize:11, padding:'4px 10px', borderRadius:999, border:'1px solid #193255', background:'#193255', color:'#fff', fontWeight:700, cursor:'pointer' }}
                    onClick={() => {
                      setSelectedAthleteId(null);
                      setSelectedAthleteInfo(null);
                      setActiveSection('calendar');
                    }}
                  >
                    {t('trainerDashboard.clearSelection')}
                  </button>
                )}
              </div>

              <div className="td-ath-list">
                {athletes.map((a) => {
                  const fullName = `${a.firstName || ''} ${a.lastName || ''}`.trim() || 'Atleta';
                  const imgUrl = getAthletePhoto(a);
                  return (
                    <div
                      key={a._id}
                      className={`td-ath-item ${String(selectedAthleteId) === String(a._id) ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedAthleteId(a._id);
                        setSelectedAthleteInfo({ name: fullName, photo: imgUrl });
                        localStorage.setItem('selectedAthleteId', a._id);
                        localStorage.setItem('selectedAthleteName', fullName);
                        localStorage.setItem('selectedAthletePhoto', imgUrl);
                        setActiveSection('calendar');
                      }}
                    >
                      <SafeImg
                        className="td-avatar"
                        src={imgUrl}
                        fallbackSrc={DEFAULT_AVATAR}
                        alt={fullName}
                      />
                      <span>{fullName}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="td-groups">
              <div className="td-card td-groups-scroll">
                <DashboardGroupsSidebar
                  trainerId={trainerId}
                  athletes={athletes}
                  onAddGroup={() => navigate('/trainer-groups')}
                />
              </div>
            </div>

            <div className="td-card" style={{ marginTop: 8 }}>
              <h6>{t('trainerDashboard.inviteAthlete')}</h6>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('trainerDashboard.emailPlaceholder')}
                style={{ width:'100%', borderRadius:999, border:'1px solid #e5e7eb', padding:'7px 12px', fontSize:12, marginBottom:8, boxSizing:'border-box', outline:'none' }}
              />
              <button
                style={{ width:'100%', borderRadius:999, padding:'8px 0', background:'#f9c93c', border:'none', color:'#193255', fontWeight:800, fontSize:12, cursor:'pointer' }}
                onClick={handleSendInvite}
              >
                {t('trainerDashboard.sendInvite')}
              </button>
            </div>
          </aside>

          {/* Centro */}
          <main className="td-center-card">
            {!selectedAthleteId ? (
              !hasAthletes ? (
                <div className="td-card p-3">
                  <h5 className="mb-1">{t('trainerDashboard.noAthletesTitle')}</h5>
                  <p className="text-muted mb-0">
                    {t('trainerDashboard.noAthletesText')}
                  </p>
                </div>
              ) : (
                <>
                  {(pmcLoading || cmpLoading) && (
                    <div className="text-muted mb-2" role="status">{t('trainerDashboard.loadingData')}</div>
                  )}
                  {!pmcLoading && pmcError && (
                    <div className="text-warning small mb-2">{pmcError}</div>
                  )}
                  {!cmpLoading && cmpError && (
                    <div className="text-warning small mb-2">{cmpError}</div>
                  )}

                  <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                    <div className="td-card" style={{ maxHeight:280, overflow:'hidden' }}>
                      <div style={{ fontSize:11, fontWeight:700, color:'#193255', marginBottom:4, padding:'4px 8px 0' }}>
                        📊 {t('trainerDashboard.pmcOverviewTitle')}
                      </div>
                      <canvas ref={chartRef1} style={{ maxHeight:240 }} />
                    </div>
                    <div className="td-card" style={{ maxHeight:280, overflow:'hidden' }}>
                      <div style={{ fontSize:11, fontWeight:700, color:'#193255', marginBottom:4, padding:'4px 8px 0' }}>
                        ⚡ {t('trainerDashboard.acwrTitle')}
                      </div>
                      <canvas ref={chartRef2} style={{ maxHeight:240 }} />
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="td-card">
                      <canvas ref={chartRef3} />
                    </div>
                  </div>

                  <div className="mt-3">
                    <h6>{t('trainerDashboard.topDetail')}</h6>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 10,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 12,
                        padding: 10,
                      }}
                    >
                      {rowsForDisplay.map((r, i) => (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <SafeImg
                            src={r.photo}
                            fallbackSrc={DEFAULT_AVATAR}
                            alt={r.name}
                            width={28}
                            height={28}
                            style={{ borderRadius: '50%', objectFit: 'cover' }}
                          />
                          <div style={{ fontSize: 13 }}>
                            <strong>{r.name}</strong>{' '}
                            <span className="text-muted">
                              — CTL {r.ctl} · ATL {r.atl} · TSB {r.tsb} · ACWR {r.acwr}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <WellnessVsCargaRanking
                      trainerId={trainerId}
                      onSelectAthlete={(id) => {
                        const a = athletes.find((x) => String(x._id) === String(id));
                        const fullName = a ? (`${a.firstName || ''} ${a.lastName || ''}`).trim() || 'Atleta' : 'Atleta';
                        const imgUrl = a ? getAthletePhoto(a) : DEFAULT_AVATAR;
                        setSelectedAthleteId(id);
                        setSelectedAthleteInfo({ name: fullName, photo: imgUrl });
                        localStorage.setItem('selectedAthleteId', id);
                        localStorage.setItem('selectedAthleteName', fullName);
                        localStorage.setItem('selectedAthletePhoto', imgUrl);
                        setActiveSection('pmc');
                      }}
                    />
                  </div>
                </>
              )
            ) : (
              <>
                {selectedAthleteInfo && (
                  <div className="athlete-header" style={{ display:'flex', alignItems:'center', marginBottom:12, padding:10, background:'#f8fafc', borderRadius:12 }}>
                    <SafeImg
                      src={selectedAthleteInfo.photo}
                      fallbackSrc={DEFAULT_AVATAR}
                      alt={t('athleteDashboard.profilePhotoAlt')}
                      style={{ width: 60, height: 60, borderRadius: '50%', marginRight: 12, objectFit: 'cover' }}
                    />
                    <h5 style={{ margin:0 }}>
                      {t('trainerDashboard.viewingAthlete', { name: selectedAthleteInfo.name })}
                    </h5>
                  </div>
                )}

                <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12 }}>
                  <button
                    className={`td-tab-btn${activeSection === 'calendar' ? ' td-tab-btn--active' : ''}`}
                    onClick={() => setActiveSection('calendar')}
                  >
                    {t('trainerDashboard.viewCalendar')}
                  </button>
                  <button
                    className={`td-tab-btn${activeSection === 'profile' ? ' td-tab-btn--active' : ''}`}
                    onClick={() => setActiveSection('profile')}
                  >
                    {t('trainerDashboard.viewProfile')}
                  </button>
                  <button
                    className={`td-tab-btn${activeSection === 'zones' ? ' td-tab-btn--active' : ''}`}
                    onClick={() => setActiveSection('zones')}
                  >
                    {t('trainerDashboard.viewZones')}
                  </button>
                  <button
                    className={`td-tab-btn${activeSection === 'pmc' ? ' td-tab-btn--active' : ''}`}
                    onClick={() => setActiveSection('pmc')}
                  >
                    {t('trainerDashboard.performance')}
                  </button>
                </div>

                {activeSection === 'calendar' && (
                  <div
                    className="td-calendar-drop"
                    onDragOver={handleCalendarDragOver}
                    onDrop={handleCalendarDrop}
                  >
                    <AthleteDashboard
                      key={`calendar-${selectedAthleteId}-${refreshTick}`}
                      isTrainerView={true}
                      athleteId={selectedAthleteId}
                      enableExternalDrop
                    />
                  </div>
                )}

                {activeSection === 'profile' && (
                  <AthleteProfile
                    key={`profile-${selectedAthleteId}`}
                    isTrainerView={true}
                    athleteId={selectedAthleteId}
                  />
                )}

                {activeSection === 'zones' && (
                  <TrainingZones
                    key={`zones-${selectedAthleteId}`}
                    isTrainerView={true}
                    athleteId={selectedAthleteId}
                  />
                )}

                {activeSection === 'pmc' && (
                  <>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                      <span style={{ fontSize:12, color:'#6b7280' }}>{t('trainerDashboard.viewLabel')}</span>
                      <div style={{ display:'flex', background:'#f3f4f6', borderRadius:999, padding:3, gap:2 }}>
                        <button
                          className={`td-seg-btn${pmcView === 'simple' ? ' td-seg-btn--active' : ''}`}
                          onClick={() => setPmcView('simple')}
                        >
                          {t('trainerDashboard.simple')}
                        </button>
                        <button
                          className={`td-seg-btn${pmcView === 'advanced' ? ' td-seg-btn--active' : ''}`}
                          onClick={() => setPmcView('advanced')}
                        >
                          {t('trainerDashboard.advancedAmcharts')}
                        </button>
                        <button
                          className={`td-seg-btn${pmcView === 'both' ? ' td-seg-btn--active' : ''}`}
                          onClick={() => setPmcView('both')}
                        >
                          {t('trainerDashboard.both')}
                        </button>
                      </div>
                    </div>

                    <div style={{ border:'1px solid #e5e7eb', borderRadius:12, padding:10, marginBottom:12, background:'#fff' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <button className="td-nav-btn" style={{ background:'#193255', color:'#fff', borderColor:'#193255' }} onClick={probeIA} disabled={aiLoading}>
                          {aiLoading ? t('trainerDashboard.consultingAi') : t('trainerDashboard.testAi')}
                        </button>
                        <small className="text-muted">
                          {t('trainerDashboard.aiDemoNotice.before')}
                          <code>OPENAI_API_KEY</code>
                          {t('trainerDashboard.aiDemoNotice.after')}
                          <em>{t('trainerDashboard.aiDemoNotice.demoMode')}</em>.
                        </small>
                      </div>
                      {aiText && (
                        <pre className="mt-2 small" style={{ whiteSpace: 'pre-wrap' }}>
                          {aiText}
                        </pre>
                      )}
                    </div>

                    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                      {(pmcView === 'advanced' || pmcView === 'both') && (
                        <div className="border rounded p-2">
                          <h6 className="mb-2">{t('trainerDashboard.banisterModelTitle')}</h6>
                          <PMCPaneAmCharts
                            key={`pmc-adv-${selectedAthleteId}`}
                            athleteId={selectedAthleteId}
                          />
                        </div>
                      )}

                      {(pmcView === 'simple' || pmcView === 'both') && (
                        <div className="border rounded p-2">
                          <h6 className="mb-2">{t('trainerDashboard.classicPmcTitle')}</h6>
                          <PMCPane
                            key={`pmc-simple-${selectedAthleteId}`}
                            athleteId={selectedAthleteId}
                          />
                        </div>
                      )}
                    </div>

                    <WellnessVsCargaCard
                      athleteId={selectedAthleteId}
                      athleteName={selectedAthleteInfo?.name}
                    />
                  </>
                )}
              </>
            )}
          </main>

          {/* Derecha */}
          <aside className={`td-right${sidebarRightOpen ? '' : ' td-right--collapsed'}`} style={{ position:'relative', overflow:'hidden', minWidth: sidebarRightOpen ? '' : 0, width: sidebarRightOpen ? '' : 0, padding: sidebarRightOpen ? '' : 0 }}>
            <button
              type="button"
              onClick={() => setSidebarRightOpen(o => !o)}
              style={{ position:'fixed', right: sidebarRightOpen ? 'clamp(260px,18vw,300px)' : 8, top:'50%', transform:'translateY(-50%)', background:'#193255', color:'#fff', border:'none', borderRadius:'6px 0 0 6px', padding:'10px 5px', cursor:'pointer', fontSize:13, lineHeight:1, zIndex:200 }}
              title={sidebarRightOpen ? 'Ocultar biblioteca' : 'Mostrar biblioteca'}
            >
              {sidebarRightOpen ? '›' : '‹'}
            </button>
            {sidebarRightOpen && <TrainerLibraryPanel athleteId={selectedAthleteId || null} />}
          </aside>
        </div>
      )}
    </div>
  );
};

export default TrainerDashboard;
